import * as React from 'react'
import {
  Box,
  useColorModeValue as mode,
} from '@chakra-ui/react'
import Layout from '../components/website/Layout'
import Headers from '../components/website/sections/Header/'
import { CourseHero } from '../components/website/legacy/CourseHero'
import { CourseSyllabus } from '../components/website/legacy/syllabus/CourseSyllabus'
import { Pricing } from '../components/website/legacy/pricing/Pricing'
import { Teacher } from '../components/website/legacy/Teacher'
import { LearnDataScience } from '../components/website/legacy/features/LearnDataScience'
import Faq from '../components/website/legacy/Faq'
import Footer from '../components/website/sections/Footer'

const DataScienceLiveCourse = () => {
  return (
    <Layout
      title="Jungle Program – Data Science Live Course"
      canonical="https://www.jungleprogram.com/data-science-live-course"
      description="Join an 8-week training to practice and gain strong foundations in data science and machine learning."
    >
      <Headers />
      <Box bg={mode('gray.50')} pt="5" pb="0" mb="0" overflow="hidden">
        <CourseHero
          level='Intermediate'
          duration='8 weeks part time'
          instructor={`Amric Trudel`}
          instructorImg={`/img/instructor-amric.png`}
          courseName={`Practical Data Science`}
          dates={`April 2022`}
          desc={<div>
            <p>
              Join an 8-week part-time training to practice and gain strong foundations in data science and machine learning.
            </p>
            <p>
              Coding and math may seem intimidating at first, this live training will help you to become a key player in your Data Science team.
            </p>
            <p>
              Part-time course compatible with your full-time job.
            </p>
          </div>}
          enroll={`https://calendly.com/gauroq/first-call-jungle-program?a1=I'm%20interested%20in:%20Practical%20Data%20Science`}
        />
        <LearnDataScience
          title={`Learn Data Science`}
          who={`This course is perfect for the beginner coder looking to start a career in Data Science, the software engineer curious about Machine Learning technologies, or the AI enthusiast searching for more practical experience.`}
          requirements={`Beginner programming skills are required. A little python experience (can you define a function?), and some statistical basics (what's a standard deviation?) are recommended.`}
          buttonLabel={`🐍 Test my Python skills`}
          buttonURI={`https://colab.research.google.com/github/JungleProgram/practical-data-scientist/blob/main/prepwork/prepwork.ipynb`}
        />
        <CourseSyllabus
          overview={
            {
              'title': 'Detailed course program',
              'subtitle': 'You will learn how to tame all types of data and reveal their secrets with Python 🐍',
              'features': [
                '<div>You will first learn to manipulate and visualise <strong>tabular</strong>, <strong>time series</strong>, <strong>image</strong>, <strong>text</strong>, and <strong>geospatial datasets</strong>.</div>',
                'Then, our first steps into Machine Learning: <strong>clustering</strong>, <strong>dimensionality reduction</strong>, and <strong>anomaly detection</strong> methods to level-up our exploratory <strong>data analysis</strong>.',
                'Next, you will train on regression and classification models to implement <strong>predictive analytics</strong>.',
                'Finally, you will learn to build powerful and accurate Machine Learning models by digging into <strong>practical evaluation and optimisation techniques</strong>.',
                'At last, the <strong>final project</strong> will reinforce our new data science skills by analysing a public dataset from scratch.'
              ]
            }
          }
          chapters={
            [
              {
                date: 'Week 1',
                name: 'Data munging',
                desc: 'The nitty-gritty of data analysis: this chapter teaches you how to load, clean, and manipulate basic datasets in python.',
                features: [
                  {
                    name: 'Introduction to Numpy & Pandas',
                    desc: `Get acquainted with the two libraries at the heart of data analysis in python: NumPy and pandas:
                    • Explain the differences between ndarrays, Series, and DataFrames
                    • Construct and carry out basic operations with these data structures
                    • Understand the need for efficient libraries for maths and data analysis.`
                  },
                  {
                    name: 'Tabular Data Pt.1',
                    desc: `Dig into the data exploration powers of pandas:
                    • Index into and select from DataFrames
                    • Clean and replace missing values
                    • Combine and merge datasets`
                  },
                  {
                    name: 'Tabular Data Pt. 2',
                    desc: `Test your data exploration skills by analysing the top 50 songs of the 2019 spotify dataset with pandas: 
                    • Group and aggregate rows
                    • Calculate new variables
                    • Answer statistical questions about the dataset`
                  }
                ]
              },
              {
                date: 'Week 2',
                name: 'Data exploration',
                desc: 'Beyond tables: learn how to extract and communicate key insights from time-series, text, and image datasets.',
                features: [
                  {
                    name: 'Time series data',
                    desc: `Organise time-series data with pandas:
                    • Manipulate time indices
                    • Calculate rolling statistics
                    • Resample time series
                    • Interpolate missing values`
                  },
                  {
                    name: 'Text & image data',
                    desc: `Make your first steps into the worlds of Natural Language Processing and Computer Vision:
                    • Operate on python strings and build regular expressions
                    • Tokenize and find entities in text with spacy
                    • Manipulate images with pillow`
                  },
                  {
                    name: 'Data visualisation',
                    desc: `Leverage data visualisation to explore and communicate:
                    • Learn fundamentals of data and visual literacy 
                    • Create graphs with matplotlib
                    • Format plots to make them more effective
                    • Create advanced charts`
                  }
                ]
              },
              {
                date: 'Week 3',
                name: 'Unsupervised learning',
                desc: 'Analytical power-up: this chapter adds clustering, dimensionality reduction, and anomaly detection to your data analysis techniques.',
                features: [
                  {
                    name: 'Clustering',
                    desc: `Cluster a geospatial dataset using k-Means:
                    • Explore data using clustering
                    • Implement k-means with sklearn
                    • Visualize geospatial data
                    • Build a ridgeline graph`
                  },
                  {
                    name: 'Dimensionality reduction',
                    desc: `Reduce the dimensionality of a dataset using PCA:
                    • Use dimensionality reduction to compress images
                    • Implement PCA on an emoji dataset with sklearn
                    • Analyse the cumulative explained variance of a PCA model
                    • Use dimensionality reduction for data visualization`
                  },
                  {
                    name: 'Anomaly detection',
                    desc: `Use gaussian distributions to detect anomalies in an image dataset:
                    • Implement Gaussian distribution anomaly detection with sklearn
                    • Debug the optimisation of a learning algorithm
                    • Discuss the imperfection of learning algorithms
                    • Learn about other outlier detection methods`
                  }
                ]
              },
              {
                date: 'Week 4',
                name: 'Supervised learning',
                desc: 'Moving past data summaries: this chapter introduces predictive models to solve fundamental machine learning tasks.',
                features: [
                  {
                    name: 'Supervised learning fundamentals',
                    desc: `Understand the different types of learning algorithms and machine learning tasks:
                    • Define features & labels
                    • Compare supervised and unsupervised learning
                    • Differentiate classification and regression
                    • Explain the basics of optimisation procedures`
                  },
                  {
                    name: 'Linear regression',
                    desc: `Use linear regression to predict the happiness of countries:
                    • Describe a regression problem
                    • Fit a linear regression model to a dataset
                    • Visualise the predictions of a trained linear regression model`
                  },
                  {
                    name: 'Logistic Regression',
                    desc: `Use logistic regression to predict the quality of wines:
                    • Describe classification problem
                    • Fit a logistic regression model to a dataset
                    • Visualise the decision boundary of a trained logistic regression model`
                  },
                ]
              },
              {
                date: 'Week 5',
                name: 'Model Training',
                desc: 'Taming the beast: this chapter shows how build accurate and effective machine learning models.',
                features: [
                  {
                    name: 'Evaluation fundamentals',
                    desc: `Evaluate machine learning models to predict their expected future performance:
                    • Split train, validation, and test sets with sklearn
                    • Run end to end machine learning experiments
                    • Compare model quality
                    • Tune a hyperparameter`
                  },
                  {
                    name: 'Learning better pt. 1',
                    desc: `Boost your features with scaling and polynomials to improve model accuracy:
                    • Scale and descale a feature matrix using standardization
                    • Calculate polynomial features
                    • Use both methods to optimise the fitting of a regression model`
                  },
                  {
                    name: 'Learning better pt. 2',
                    desc: `Regularize a model to improve its generalisation properties:
                    • Overfit a polynomial regression model
                    • Use ridge regularization to prevent overfitting
                    • Analyse the effect of the regularization weight on model parameter sizes
                    • Train a regularized logistic regression model with polynomial features`
                  },
                ]
              },
              ,
              {
                date: 'Week 6',
                name: 'Non-Linear Models',
                desc: 'Expanding your model vocabulary: this chapter goes through Naive Bayes, Decision Trees, and Support Vector Machines (SVMs), and boosts them with ensemble methods.',
                features: [
                  {
                    name: 'Support Vector Machines',
                    desc: `Use SVMs to detect fraudulent banknotes:
                    • Train linear & RBF kernel SVMs
                    • Visualize and compare large margin classifier decision boundaries 
                    • Analyse the effect of regularization hyperparameters`
                  },
                  {
                    name: 'Naive Bayes & Decision Trees',
                    desc: `Continue detecting fake banknotes with Naive Bayes & Decision Trees:
                    • Train & evaluate a Naive Bayes classifier
                    • Train & evaluate a Decision Tree
                    • Carry out error analysis to better understand model hyperparameters`
                  },
                  {
                    name: 'Ensemble Methods',
                    desc: `Strength in numbers, combine models to improve accuracy:
                    • Use bagging to train random forest models
                    • Use boosting to train AdaBoost and XGBoost models
                    • Analyse accuracy vs performance tradeoffs in ensemble models`
                  },
                ]
              },
              {
                date: 'Week 7 & 8',
                name: 'Final project',
                desc: 'This chapter tests your skills on a public dataset by completing an exploratory data analysis report, and training at least one Machine Learning model.',
                features: [

                ]
              }
            ]
          }
        />
        <Teacher
          name={`Amric Trudel`}
          desc={<div>
            <p>
              Amric Trudel graduated from <strong>McGill University</strong> in Montréal and studied Computer Science at <strong>42 Paris</strong>.
            </p>
            <p>
              He has been the president of <strong>42 Artificial Intelligence</strong>, an association in which he developed training programs on <strong>Machine Learning</strong> and supervised the creation of bootcamps for his fellow students.
            </p>
            <p>
              Amric has also given Machine Learning initiation workshops to large company executives. He now works at <strong>OCTO Technology</strong> as a Data Science consultant and industrializes data-oriented projects for clients like InVivo, Total, and Accenture.
            </p>
          </div>}
          image={`./img/instructor-amric-2.jpg`}
          linkedin={`https://www.linkedin.com/in/amrictrudel/`}
        />
        <Pricing
          name="Practical Data Science"
          price="1290"
          pricestroke=""
          duration="Next training: April 2022"
          link="https://calendly.com/gauroq/first-call-jungle-program?a1=I'm%20interested%20in:%20Practical%20Data%20Science"
          features={
            [
              'Part-time training',
              'Join a micro-class of < 10 students',
              'Compatible with your full-time job',
              '3 hours of live interactive class per week',
              '3 to 5 hours of coding exercises per week',
              'Demo day & certification of course completion',
              'Connect with other students and learn together',
              'Master the latest tools & languages in machine learning',
              '1:1 calls with the teachers',
              '5-day free trial',
              'Eligible to CPF financing in France 🇫🇷'
            ]
          }
        />
        <Faq price="1290" />
        <Footer />
      </Box>
    </Layout>
  )
}

export default DataScienceLiveCourse